import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gem, Leaf, HeartPulse, Compass, TrendingUp, Save, Printer, ArrowUpRight } from "lucide-react";
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer } from "recharts";
import { useCentralState } from "@/context/CentralStateContext";
import { usePeriod } from "@/context/PeriodContext";
import { computeCentralSnapshot, centerMeta, scoreTone, statusStyles } from "@/lib/central";
import { periodLabel } from "@/lib/wellness";
import { fmtMoney } from "@/lib/finance";
import { centralApi } from "@/lib/api";
import { PageHeader, SectionCard, ProgressBar } from "@/components/shared";
import { PeriodNav } from "@/components/PeriodNav";
import { FormModal, TextField } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const Ring = ({ value, label = "index" }) => {
  const r = 58, c = 2 * Math.PI * r, off = c - (value / 100) * c;
  return (
    <div className="relative h-[150px] w-[150px]">
      <svg viewBox="0 0 144 144" className="h-full w-full -rotate-90">
        <circle cx="72" cy="72" r={r} fill="none" stroke="rgba(23,35,31,0.08)" strokeWidth="13" />
        <circle cx="72" cy="72" r={r} fill="none" stroke="var(--green)" strokeWidth="13" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} className="transition-all duration-500" />
      </svg>
      <div className="absolute inset-0 grid place-items-center"><div className="text-center"><div className="font-serif-display text-4xl text-[var(--text)] tabular-nums" data-testid="central-overall">{value}</div><div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">{label}</div></div></div>
    </div>
  );
};

const icons = { Wealth: Gem, Life: Leaf, Wellness: HeartPulse };

export default function CentralCommand() {
  const { fin, life, wellness, loading } = useCentralState();
  const { horizon, setHorizon, date, setDate } = usePeriod();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);

  if (loading || !fin) return <Skeleton className="h-96 rounded-[14px]" />;

  const snap = computeCentralSnapshot(fin, life, wellness, date);
  const cards = [
    { area: "Wealth", score: snap.wealth.score, kpis: [["Net worth", fmtMoney(snap.wealth.netWorth)], ["Savings rate", `${snap.wealth.savingsRate}%`], ["Emergency fund", `${snap.wealth.months} mo`], ["Utilization", `${snap.wealth.util}%`]] },
    { area: "Life", score: snap.life.score, kpis: [["Energy", `${snap.life.energy}%`], ["Alignment", `${snap.life.alignment}%`], ["Today's points", `${snap.life.earned}/${snap.life.planned}`], ["Needs a lift", snap.life.lowest || "—"]] },
    { area: "Wellness", score: snap.wellness.score, kpis: [["Readiness", `${snap.wellness.readiness}%`], ["Goal progress", `${snap.wellness.goalAvg}%`], ["Glow rituals", `${snap.wellness.glowPct}%`]] },
  ];
  const radarData = (snap.life.areaScores || []).map((a) => ({ area: a.short || a.name, score: a.score }));

  const saveReport = async () => {
    setSaving(true);
    try {
      await centralApi.saveReport({ title: title.trim() || `${horizon} report — ${periodLabel(horizon, date)}`, horizon, date, snapshot: snap });
      toast.success("Report saved");
      setOpen(false); setTitle("");
    } catch { toast.error("Could not save report"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="central-command">
      <PageHeader title="Command Central" subtitle={`Everything together · ${periodLabel(horizon, date)}`} testid="central-command-header"
        actions={<div className="flex items-center gap-2 print:hidden">
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-report-button" className="border-[color:var(--border-subtle)]"><Printer className="h-4 w-4 mr-1.5" />Export / Print</Button>
          <Button size="sm" onClick={() => setOpen(true)} data-testid="save-report-button" className="bg-[var(--green)] text-[#f7fbf9] hover:bg-[color:rgba(31,115,85,0.9)]"><Save className="h-4 w-4 mr-1.5" />Save as report</Button>
        </div>} />

      <div className="mb-4 print:hidden"><PeriodNav horizon={horizon} setHorizon={setHorizon} date={date} setDate={setDate} testid="central-period" /></div>

      <SectionCard className="mb-4" testid="central-overall-card">
        <div className="flex flex-wrap items-center gap-6">
          <Ring value={snap.overall} label="whole-life" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]"><Compass className="h-3.5 w-3.5" />Whole-life index</div>
            <h3 className="font-serif-display text-2xl text-[var(--text)] mt-1">{snap.overall >= 70 ? "You are thriving across the board." : snap.overall >= 45 ? "Steady — a few areas want attention." : "Let's rebuild momentum, gently."}</h3>
            <p className="text-sm text-[var(--text-muted)] mt-1">A balanced blend of your Wealth, Life and Wellness scores for {periodLabel(horizon, date)}.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {cards.map((c) => (<span key={c.area} className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs" style={{ backgroundColor: statusStyles[scoreTone(c.score)].bg, color: statusStyles[scoreTone(c.score)].color }} data-testid={`central-chip-${c.area}`}>{c.area} {c.score}</span>))}
            </div>
          </div>
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {cards.map((c) => { const Icon = icons[c.area]; const meta = centerMeta[c.area]; return (
          <SectionCard key={c.area} testid={`central-area-${c.area}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5"><span className="grid place-items-center h-10 w-10 rounded-[12px] text-[#f7fbf9]" style={{ backgroundColor: meta.color }}><Icon className="h-5 w-5" /></span><div><div className="font-serif-display text-lg text-[var(--text)]">{c.area}</div><div className="text-[11px] text-[var(--text-muted)]">{statusStyles[scoreTone(c.score)].label}</div></div></div>
              <div className="font-serif-display text-3xl tabular-nums text-[var(--text)]" data-testid={`central-score-${c.area}`}>{c.score}</div>
            </div>
            <div className="mt-3"><ProgressBar value={c.score} max={100} tone={c.score >= 70 ? "green" : "gold"} /></div>
            <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2">
              {c.kpis.map(([k, v]) => (<div key={k} className="min-w-0"><div className="text-[11px] text-[var(--text-faint)] truncate">{k}</div><div className="text-sm text-[var(--text)] tabular-nums truncate">{v}</div></div>))}
            </div>
            <button onClick={() => navigate(meta.route)} className="mt-4 inline-flex items-center gap-1 text-sm font-medium print:hidden" style={{ color: meta.color }} data-testid={`central-open-${c.area}`}>Open {c.area} <ArrowUpRight className="h-3.5 w-3.5" /></button>
          </SectionCard>
        ); })}
      </div>

      <SectionCard title="Whole-life balance" subtitle="Your eight life areas at a glance" testid="central-radar-card">
        {radarData.length ? (
          <div className="h-80" data-testid="central-radar">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={320}>
              <RadarChart data={radarData} outerRadius="72%">
                <PolarGrid stroke="rgba(23,35,31,0.12)" />
                <PolarAngleAxis dataKey="area" tick={{ fontSize: 12, fill: "var(--text-muted)" }} />
                <Radar dataKey="score" stroke="var(--green)" fill="var(--green)" fillOpacity={0.25} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        ) : <p className="text-sm text-[var(--text-muted)]">Add life-area data to see your balance chart.</p>}
      </SectionCard>

      <FormModal open={open} onOpenChange={setOpen} title="Save this as a report" onSubmit={saveReport} submitLabel={saving ? "Saving…" : "Save report"} testid="save-report-modal"
        description="Capture a snapshot of all three centers for this period so you can revisit it later.">
        <TextField label="Report title" value={title} onChange={(e) => setTitle(e.target.value)} full testid="report-title-input" placeholder={`${horizon} report — ${periodLabel(horizon, date)}`} />
      </FormModal>
    </div>
  );
}
